// What the sender wrote to the signers and the sign-by deadline, beside the
// document while it waits. The initiator changes either while the workflow
// is open; after the deadline nobody can sign until they move it.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlarmClock, Loader2, MessageSquare, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FieldError } from '@/shared/ui/FieldError';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors';
import { usePatchRequest } from '@/shared/hooks/requests';
import { WORKFLOW } from '@/shared/utils/urls';
import { currentUserUuid, iHold } from '@/shared/auth/me.js';
import { SCOPE } from '@/shared/auth/scopes.js';
import { field as get, signingState } from '../signingState';
import { MAX_MESSAGE_LENGTH, deadlineProblem, noticePayload, toLocalInput } from '../notice';

const FIELDS = ['message', 'expires_at'];

export function WorkflowNotice({ collection, fileUuid, isGuest = false, guestToken = null, onChanged }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const headers = guestToken ? { Authorization: `Bearer ${guestToken}`, 'accept-language': i18n.language } : undefined;
  const patch = usePatchRequest({ url: WORKFLOW.replace('{file_uuid}', fileUuid), ...(headers ? { headers } : {}) });

  const problem = deadlineProblem(expiresAt);
  const checks = useFieldErrors(problem ? { expires_at: t(`signing:notice.deadline_${problem}`) } : {});

  if (!collection) return null;
  const text = get(collection, 'message');
  const deadline = get(collection, 'expires_at');
  const passed = Boolean(get(collection, 'deadline_passed'));
  const { ended } = signingState(collection);
  const waiting = !ended && !get(collection, 'is_completed');

  const me = isGuest ? null : currentUserUuid();
  const isInitiator = me != null && String(get(collection, 'initiator_user_uuid')) === String(me);
  const canEdit = isInitiator && waiting && iHold(SCOPE.SIGNING_WRITE);
  if (!text && !(deadline && waiting) && !canEdit) return null;

  const when = deadline ? new Date(deadline).toLocaleString(i18n.language) : null;

  const edit = () => {
    setMessage(text || '');
    setExpiresAt(toLocalInput(deadline));
    checks.reset();
    setOpen(true);
  };

  const save = async () => {
    if (!checks.attempt()) return;
    const res = await patch.request({ data: noticePayload({ message, expiresAt }) });
    if (!res.success) {
      checks.showServer(res, FIELDS);
      return;
    }
    toast.success(t('signing:notice.saved'));
    setOpen(false);
    onChanged?.(res.response);
  };

  return (
    <>
      {(text || (deadline && waiting)) && (
        <div className="text-sm rounded-md border border-border bg-muted/40 p-3 space-y-2" data-testid="workflow-notice">
          {text && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                {t('signing:notice.fromSender')}
              </div>
              <p className="text-foreground whitespace-pre-wrap break-words">{text}</p>
            </div>
          )}
          {deadline && waiting && (
            <div
              className={`flex items-center gap-2 text-xs ${passed ? 'font-medium text-red-700 dark:text-red-400' : 'text-foreground'}`}
              data-testid="workflow-deadline"
            >
              <AlarmClock className="w-3.5 h-3.5 shrink-0" />
              {passed
                ? t(isInitiator ? 'signing:notice.passedInitiator' : 'signing:notice.passed', { when })
                : t('signing:notice.signBy', { when })}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <Button variant="ghost" size="sm" className="w-full justify-start text-xs" onClick={edit}>
          <Pencil className="w-3.5 h-3.5 mr-2" />
          {t('signing:notice.edit')}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" zIndex={200}>
          <DialogHeader>
            <DialogTitle className="text-base">{t('signing:notice.editTitle')}</DialogTitle>
            <DialogDescription>{t('signing:notice.editHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="workflow-notice-message">{t('signing:addSigners.message')}</Label>
            <Textarea
              id="workflow-notice-message"
              value={message}
              maxLength={MAX_MESSAGE_LENGTH}
              rows={3}
              onChange={(e) => {
                checks.touch('message');
                setMessage(e.target.value);
              }}
              placeholder={t('signing:addSigners.messagePlaceholder')}
              {...checks.describe('message', 'workflow-notice')}
            />
            <FieldError id="workflow-notice-message-error" message={checks.errors.message} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="workflow-notice-expires_at">{t('signing:addSigners.expiresAt')}</Label>
            <Input
              id="workflow-notice-expires_at"
              type="datetime-local"
              value={expiresAt}
              min={toLocalInput(new Date())}
              onChange={(e) => {
                checks.touch('expires_at');
                setExpiresAt(e.target.value);
              }}
              {...checks.describe('expires_at', 'workflow-notice')}
            />
            <FieldError id="workflow-notice-expires_at-error" message={checks.errors.expires_at} />
            <p className="text-xs text-muted-foreground">{t('signing:addSigners.expiresHint')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={patch.loading}>
              {t('signing:actions.cancel')}
            </Button>
            <Button onClick={save} disabled={patch.loading || Boolean(problem)}>
              {patch.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('signing:notice.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default WorkflowNotice;
